import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import {
  FormikCheckboxField,
  FormikDateTimeField,
  FormikSelectField,
  FormikTextField,
} from "src/components/FormikComponents/FormikWrappers";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { TextField } from "src/components/FormikComponents/TextField";
import { Button, CardContent, Grid, Typography, useTheme, Theme, alpha } from "@mui/material";
import {
  ClientSettingName,
  IAircraft,
  IClientSettings,
  IFlightAircraft,
  IFlightEditorSettingsValue,
  IFlightFlag,
  IFlightType,
  IFullAircraftConfiguration,
  minuteInMilliseconds,
  PolicyResource,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { validateDateTimeFormat } from "src/utils/index";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { DisplayLastModification, ILastModifiedInfo } from "src/components/FormikComponents/DisplayLastModification";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAirportOption, IStructuresState } from "src/redux/reducers/StructuresReducer";
import { formatIdentFromAirport, updateAirportOptions } from "src/utils/airportHelper";
import { getFormFieldDiff, milli2DaysHrsMin } from "src/utils/index";
import { filterApplicableFields, IEditPartialFlight, IFullFlight } from "../BetaEventEditor";
import { FormHeader, FormikFormIsDirtyEffect, FormTabId, IFormHandle } from "../helpers";
import { getClientSetting } from "src/redux/actions/App.Actions";

interface IFlightTabProps {
  fullFlight: IFullFlight;
  setLoadedFlight: (flightValues: IFullFlight) => void;
  updateTabState: (dirtyStatus: boolean) => void; // update parent with dirty status.
  mosaicId: string;
  setAPICallStatus: (statusMessage: string) => void;
  renderDialogModal: (title: string, message: string) => void;
  setAPISModalOpen: (modalState: boolean) => void;
  hasAPISPermissions: boolean;
  isAPISCustomer: boolean;
  lastModifiedInfo: ILastModifiedInfo;
  registerFormHandle?: (tabId: string, handle: IFormHandle | null) => void;
  saveAllDirtyTabs?: (excludeTabId?: string) => Promise<void>;
}

//set initial value when user click "new flightType"
const initialFlightValues: IEditPartialFlight = {
  _id: undefined,
  flightNumber: undefined,
  flightNumberSuffix: undefined,
  isReleased: false,
  flightType: undefined,
  origin: undefined,
  departureGate: undefined,
  destination: undefined,
  arrivalGate: undefined,
  divertedFrom: undefined,
  diversionReason: undefined,
  diversionComment: undefined,
  scheduledOut: undefined,
  scheduledOff: undefined,
  scheduledOn: undefined,
  scheduledIn: undefined,
  receivedDepartureMessage: false,
  actualOut: undefined,
  actualOff: undefined,
  actualOn: undefined,
  actualIn: undefined,
  receivedArrivalMessage: false,
  aircraft: undefined,
  aircraftConfiguration: undefined,
  customer: [],
  note: undefined,
  flags: [],
};

const FlightFormFieldsStyles = (theme: Theme): any => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignContent: "flex-start",
  position: "relative",
  padding: "10px",
  flexGrow: 1,
  "& .MuiGrid-item": { width: 363 },
  "& .dialog_spinner_progress": { margin: "-10px" },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& .formik_field": {
    width: 363,
    "&.note, &.comment": { width: 736 },
  },
  "& p.required_fields_label, & .crewslot_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .required_fields, & .crewslot_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
  },
  "& .flight_number": {},
  "& .day_offset, .block_duration": {
    width: "auto",
    "& .input_control": {
      "& label": { width: 80 },
      "& div.input-base": {
        maxWidth: 100,
      },
    },
  },
  "& .oc_datetime": {
    // used in flight
    alignItems: "flex-start",
  },
  "& .scheduled_out, & .scheduled_in": {
    // used in recurring flight
    width: "auto",
    alignItems: "flex-start",
    "& .input_control": {
      "& label": { width: 80 },
      "& .time-input": { width: 70 },
    },
  },
  "& .position_count": {
    width: "auto",
    "& label": { width: "auto" },
    "& .input_control": {
      "& div.input-base": {
        maxWidth: 50,
      },
    },
  },
  "& .taxiTime": {
    maxWidth: 100,
    "& .input_control": {
      "& label": { width: "auto" },
      "& div.input-base.MuiInputBase-fullWidth": {
        width: 40,
        flexGrow: "unset",
      },
    },
  },
  "& .gate": {
    flexGrow: "reset",
    "& div.react-select-container": {
      marginRight: "0px",
    },
    "& label": {
      width: "50px !important",
    },
  },
});

type FullFlightForm = FormikProps<IEditPartialFlight>;
type RestorableFieldName = keyof Pick<IEditPartialFlight, "flightNumberSuffix">;

/**
 * Function which renders the FlightFlag (saved as a string), into it's SVG Element
 * @param svgString - SVG string saved in the data.
 * @returns - SVG Element rendered on screen.
 */
export const renderFlagIcon = (svgString: string): JSX.Element => {
  if (svgString) {
    return <div dangerouslySetInnerHTML={{ __html: svgString }} />;
  }
  return null;
};

////////////////////////////
// FORM Flight Validation //
////////////////////////////
const validateFlight = (values: IEditPartialFlight, initialValues: IEditPartialFlight): any => {
  const {
    flightNumber,
    flightNumberSuffix,
    origin,
    destination,
    scheduledOut,
    scheduledOff,
    scheduledOn,
    scheduledIn,
    actualOut,
    actualOff,
    actualOn,
    actualIn,
    flightType,
    aircraft,
    aircraftConfiguration,
    note,
    divertedFrom,
    diversionReason,
    diversionComment,
  } = values;
  // nested values have to be added here as empty objects.
  const errors: { [key: string]: any } = {};

  // Only validate if the form is dirty (has changes)
  if (!flightNumber && flightNumber !== 0) {
    errors.flightNumber = "Requires a flight number.";
  }
  if (flightNumber && flightNumber.toString().length > 5) {
    errors.flightNumber = "Flight number must be less than 5 digits.";
  }
  if (flightNumberSuffix && !/^[A-Za-z]$/.test(flightNumberSuffix)) {
    errors.flightNumberSuffix = "Flight # suffix must be a single letter (A-Z).";
  }
  if (!flightType) {
    errors.flightType = "Requires an assigned flight type.";
  }
  if (!origin) {
    errors.origin = "Requires origin ICAO.";
  }
  if (!destination) {
    errors.destination = "Requires destination ICAO.";
  }

  if (!scheduledOut) {
    errors.scheduledOut = "Requires scheduled leave gate date/time.";
  }
  if (!scheduledOff) {
    errors.scheduledOff = "Requires scheduled departure date/time.";
  }
  if (!scheduledOn) {
    errors.scheduledOn = "Requires scheduled arrival date/time.";
  }
  if (!scheduledIn) {
    errors.scheduledIn = "Requires scheduled enter gate date/time.";
  }

  if (!actualOut) {
    errors.actualOut = "Requires actual leave gate date/time.";
  }
  if (!actualOff) {
    errors.actualOff = "Requires actual departure Date/time.";
  }
  if (!actualOn) {
    errors.actualOn = "Requires actual arrival Date/time.";
  }
  if (!actualIn) {
    errors.actualIn = "Requires actual enter gate date/time.";
  }

  if (divertedFrom && !diversionReason) {
    errors.diversionReason = "Requires diversion reason.";
  }

  //only require a comment if the field diverted from field is true and dirty
  //I am not sure if this is the best way to see if the field is dirty or not
  if (divertedFrom && divertedFrom !== initialValues.divertedFrom && !diversionComment) {
    errors.diversionComment = "Requires a comment.";
  }

  // Validate Date/Time values.
  const dateTimeArry = [
    "scheduledOut",
    "scheduledOff",
    "scheduledOn",
    "scheduledIn",
    "actualOut",
    "actualOff",
    "actualOn",
    "actualIn",
  ];

  for (const field of dateTimeArry) {
    const typedField = field as keyof typeof values;
    // Display any inline errors triggered by the component when validating the date and time inputs.
    if (values[typedField] && !!validateDateTimeFormat(values[typedField]).errorString) {
      errors[typedField] = validateDateTimeFormat(values[typedField]).errorString;
    }
  }

  // SCHEDULED CHECKS

  if (!!scheduledOut && !!scheduledOff) {
    if (new Date(scheduledOff) < new Date(scheduledOut)) {
      errors.scheduledOff = "Flight cannot depart before it leaves gate.";
      errors.scheduledOut = "Flight cannot depart before it leaves gate.";
    }
  }

  // Check if scheduled arrival is after departure.
  if (!!scheduledOff && !!scheduledOn) {
    if (new Date(scheduledOn) <= new Date(scheduledOff)) {
      errors.scheduledOff = "Flight must depart before its arrival.";
      errors.scheduledOn = "Flight must arrive after its departure.";
    }
  }

  if (!!scheduledOut && !!scheduledIn) {
    if (new Date(scheduledIn) <= new Date(scheduledOut)) {
      errors.scheduledOut = "Flight must leave gate before returning.";
      errors.scheduledIn = "Flight must return to gate after leaving.";
    }
  }

  if (!!scheduledOn && !!scheduledIn) {
    if (new Date(scheduledIn) < new Date(scheduledOn)) {
      errors.scheduledOn = "Flight cannot enter gate before it lands.";
      errors.scheduledIn = "Flight cannot enter gate before it lands.";
    }
  }

  // ACTUAL CHECKS

  if (!!actualOut && !!actualOff) {
    if (new Date(actualOff) < new Date(actualOut)) {
      errors.actualOff = "Flight cannot depart before it leaves gate.";
      errors.actualOut = "Flight cannot depart before it leaves gate.";
    }
  }

  // Check if actual arrival is after departure.
  if (!!actualOff && !!actualOn) {
    if (new Date(actualOn) <= new Date(actualOff)) {
      errors.actualOff = "Flight must depart before its arrival.";
      errors.actualOn = "Flight must arrive after its departure.";
    }
  }

  // Check if actual actualOut is after actualIn.
  if (!!actualOut && !!actualIn) {
    if (new Date(actualIn) <= new Date(actualOut)) {
      errors.actualOut = "Flight must leave gate before returning.";
      errors.actualIn = "Flight must return to gate after leaving.";
    }
  }

  if (!!actualOn && !!actualIn) {
    if (new Date(actualIn) < new Date(actualOn)) {
      errors.actualOn = "Flight cannot enter gate before it lands.";
      errors.actualIn = "Flight cannot enter gate before it lands.";
    }
  }

  // Validate small note size
  if (!!note && note.length > 100) {
    errors.note = "Small note cannot exceed 100 characters.";
  }

  if (aircraft && !aircraftConfiguration) {
    errors.aircraftConfiguration = "Selected aircraft is missing aircraft configuration.";
  }

  return errors;
};

/**
 * Base Flight Form
 * @param props -
 */
export const FlightTab = (props: IFlightTabProps): JSX.Element => {
  const {
    fullFlight,
    setLoadedFlight,
    updateTabState,
    mosaicId,
    setAPICallStatus,
    renderDialogModal,
    setAPISModalOpen,
    hasAPISPermissions,
    isAPISCustomer,
    lastModifiedInfo,
    registerFormHandle,
    saveAllDirtyTabs,
  } = props;
  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);
  const [flightEditorSettings, setFlightEditorSettings] = useState<IFlightEditorSettingsValue>(null);
  const [arrivalGateCodes, setArrivalGateCodes] = useState<any[]>([]);
  const [departureGateCodes, setDepartureGateCodes] = useState<any[]>([]);
  const formikRef = useRef<FormikProps<IEditPartialFlight>>(null);
  const restorableFieldValuesRef = useRef<Partial<Record<RestorableFieldName, string>>>({});

  /**
   * Builds onFocus and onBlur handlers for text input fields which clear the field on focus,
   * and restore the previous value on blur if left empty.
   */
  const buildTransientTextFieldHandlers = useCallback(
    (fieldName: RestorableFieldName, form: FullFlightForm): { onFocus: () => void; onBlur: () => void } => {
      return {
        onFocus: () => {
          const currentValue = (form.values?.[fieldName] ?? "").toString();
          restorableFieldValuesRef.current[fieldName] = currentValue;
          if (currentValue) {
            form.setFieldValue(fieldName, "", false);
          }
        },
        onBlur: () => {
          const currentValue = (form.values?.[fieldName] ?? "").toString();
          const priorValue = restorableFieldValuesRef.current[fieldName] ?? "";
          if (!currentValue && priorValue) {
            form.setFieldValue(fieldName, priorValue, false);
          }
          delete restorableFieldValuesRef.current[fieldName];
        },
      };
    },
    []
  );

  // Register form handle so parent can trigger this tab's submit for save-all
  useEffect(() => {
    registerFormHandle?.(FormTabId.Flight, {
      submitForm: () => formikRef.current?.submitForm() ?? Promise.resolve(),
      isDirty: () => formikRef.current?.dirty ?? false,
    });
    return () => {
      registerFormHandle?.(FormTabId.Flight, null);
    };
  }, [registerFormHandle]);

  useEffect(() => {
    //load flight editor settings
    const getFlightEditorSetting = async (): Promise<void> => {
      const flightEditorSetting: IClientSettings = await getClientSetting(ClientSettingName.FlightEditor);
      setFlightEditorSettings(flightEditorSetting?.setValue);
    };
    getFlightEditorSetting();
  }, []);

  useEffect(() => {
    if (formikRef.current?.dirty) {
      formikRef.current.handleReset();
    }
  }, [fullFlight]);

  /** New filter added on fullFlight here to ensure that fields not required in payload aren't included in form initialization */
  const values: IEditPartialFlight = useMemo(
    () => ({ ...initialFlightValues, ...filterApplicableFields(fullFlight, "Flight") }),
    [fullFlight]
  );
  const { reInitializeForm, ...initialValues } = values;

  const { origin, destination, aircraft } = initialValues;

  const theme = useTheme();
  const dispatch = useDispatch();

  // Retrieve Field Permission access check.
  const hasPermissions = useSelector((state: IRootState) => state.authentication.hasPermissions);
  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { airportOptions, customerStructure } = structures;
  const { flightTypes, customers, flightFlags, diversionReasons } = customerStructure;
  const flightEventTypes = useMemo(() => {
    return flightTypes?.length
      ? flightTypes
          .filter((type: IFlightType) => type.isFlight)
          .sort((a, b) => {
            return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
          })
      : [];
  }, [flightTypes]);

  const aircraftOptions = customerStructure?.aircraft;
  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(true);

  // Retrieve initial AirportOptions for origin and destination
  useEffect(() => {
    if (origin) void updateAirportOptions(origin, airportOptions, dispatch);
  }, [origin, airportOptions, dispatch]);

  useEffect(() => {
    if (destination) void updateAirportOptions(destination, airportOptions, dispatch);
  }, [destination, airportOptions, dispatch]);

  useEffect(() => {
    const arrivalGates: any = [];
    const departureGates: any = [];
    airportOptions[fullFlight.origin]?.gates?.forEach((gate) => {
      departureGates.push({
        label: gate.code,
        value: gate.code,
      });
    });
    setDepartureGateCodes(departureGates);
    airportOptions[fullFlight.destination]?.gates?.forEach((gate) => {
      arrivalGates.push({
        label: gate.code,
        value: gate.code,
      });
    });

    setArrivalGateCodes(arrivalGates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [aircraftConfigOptions, setAircraftConfigOptions] = useState<IFullAircraftConfiguration[]>([]);
  const getAircraftConfigurations = useCallback(async (aicraftId: string): Promise<IFullAircraftConfiguration[]> => {
    const response = await AmplifyAPIWrapper.Get<IFullAircraftConfiguration[]>(
      `/aircraftConfiguration?aircraftId=${aicraftId}`
    );
    return Array.isArray(response) ? response : [];
  }, []);

  // Retrieve available configurations for initial aircraft and apply default if not assigned.
  useEffect(() => {
    if (aircraft?._id) {
      const getConfigs = async (): Promise<void> => {
        const aircraftConfigOptions = await getAircraftConfigurations(aircraft?._id);
        if (Array.isArray(aircraftConfigOptions)) {
          setAircraftConfigOptions(aircraftConfigOptions);
        }
      };
      getConfigs();
    }
  }, [aircraft, getAircraftConfigurations]);

  // Assign default configuration based on selected aircraft (only called onChange);
  const assignDefaultConfiguration = useCallback(
    async (newAC: IAircraft, flightForm: FullFlightForm): Promise<void> => {
      const aircraftConfigOptions = await getAircraftConfigurations(newAC?._id); // Get configuration options for this aircraft.
      if (Array.isArray(aircraftConfigOptions)) {
        setAircraftConfigOptions(aircraftConfigOptions);
      }

      const { setFieldValue, setFieldTouched } = flightForm;
      const ACConfig =
        newAC && aircraftOptions?.length > 0
          ? aircraftOptions.find((full) => full._id === newAC._id)?.defaultConfig
          : null;
      setFieldValue("aircraftConfiguration", ACConfig);
      setFieldTouched("aircraftConfiguration", true);
    },
    [aircraftOptions, getAircraftConfigurations]
  );

  /** HANDLE DIVERTED FLIGHTS */
  const handleSetDivertedDest = useCallback((isDiverted: boolean, flightForm: FullFlightForm): void => {
    const { setValues, setTouched, values, setFieldValue, setFieldTouched } = flightForm;
    if (isDiverted) {
      setValues({ ...values, divertedFrom: values?.destination, destination: null, diversionReason: null });
    } else {
      setValues({ ...values, divertedFrom: null, destination: values?.divertedFrom, diversionReason: null });
    }
    setFieldTouched("divertedFrom", true, false);
    setFieldTouched("destination", true, false);
    setFieldTouched("diversionReason", true, false);
  }, []);

  /** Compute new Off or In times based on supplied taxi out or in minutes */
  const computeFromTaxi = useCallback((taxiTime: number, setKey: string, flightForm: FullFlightForm) => {
    if (typeof taxiTime === "number" && setKey) {
      const { values, setFieldValue, setFieldTouched } = flightForm;
      let newValue = values[setKey as keyof typeof values];
      switch (setKey) {
        case "scheduledOff":
          newValue = moment(values?.scheduledOut).add(taxiTime, "minutes")?.valueOf();
          break;
        case "scheduledOn":
          newValue = moment(values?.scheduledIn).subtract(taxiTime, "minutes")?.valueOf();
          break;
        case "actualOff":
          newValue = moment(values?.actualOut).add(taxiTime, "minutes")?.valueOf();
          break;
        case "actualOn":
          newValue = moment(values?.actualIn).subtract(taxiTime, "minutes")?.valueOf();
          break;
      }
      setFieldValue(setKey, newValue);
      setFieldTouched(setKey, true, true);
    }
  }, []);

  // If Out/In times are modified, shift's the associated Off/On times accordingly based on current taxi values.
  const shiftOffOnTimes = useCallback((changeDelta: number, setKey: string, flightForm: FullFlightForm): void => {
    const { values, setFieldValue, setFieldTouched } = flightForm;
    const newValue = values[setKey as keyof typeof values] + changeDelta;
    // Had to wrap a timeout here as the shift call was taking please directly after a formik onchange handler and validation wasn't properly getting called as a result.
    setTimeout(() => {
      setFieldValue(setKey, newValue);
      setFieldTouched(setKey, true, true);
    }, 0);
  }, []);

  /** FLIGHT CHANGE SUBMISSION */
  const handleSubmit = useCallback(
    async (values: IEditPartialFlight, helpers: FormikHelpers<IEditPartialFlight>): Promise<void> => {
      const { setSubmitting, setStatus, resetForm } = helpers;
      setSubmitting(true);
      setAPICallStatus("Updating Flight");
      const mode = values._id ? "update" : "create";
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Flight...`,
        successMessage: `Flight Successfully ${mode === "update" ? "Updated" : "Created"}.`,
        errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Flight`,
      };

      const flightUpdateDiff = getFormFieldDiff(values, initialValues);

      // Should I pop up a modal? Make sure you check the client Name
      //TODO: Should this APIS Modal only show durning a certain time range?
      if (values?.divertedFrom !== initialValues?.divertedFrom && isAPISCustomer) {
        if (hasAPISPermissions) {
          // Pop up APIS modal
          setAPISModalOpen(true);
        } else {
          // Display APIS Warning
          renderDialogModal(
            "APIS Message Warning",
            "This flight change may require an APIS change message. You do not have permissions to send an APIS message"
          );
        }
      }
      const response =
        mode === "create"
          ? await AmplifyAPIWrapper.Post<any>("/flights/", values, notifications)
          : await AmplifyAPIWrapper.Put<any>(`/flights/${values._id}`, flightUpdateDiff, notifications);
      if (response?.newEvent?._id) {
        setLoadedFlight(response?.newEvent);
        // Ensure that fullFlight is filtered before being reloaded into form so that fields not required are removed.
        resetForm({ values: filterApplicableFields(response?.newEvent, "Flight") });
        updateTabState(false);
      }
      setSubmitting(false);
      setAPICallStatus(null);

      // Trigger save on all other dirty tabs
      await saveAllDirtyTabs?.(FormTabId.Flight)?.catch((error) =>
        console.error("Failed to save all dirty tabs:", error)
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setLoadedFlight,
      dispatch,
      setAPICallStatus,
      initialValues,
      renderDialogModal,
      setAPISModalOpen,
      hasAPISPermissions,
      isAPISCustomer,
    ]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={reInitializeForm}
      onSubmit={handleSubmit}
      validate={(currentValues) => validateFlight(currentValues, initialValues)}
      innerRef={formikRef}
    >
      {(flightForm: FullFlightForm) => {
        const { initialValues, values, dirty, isValid, isSubmitting, handleReset } = flightForm;
        const blockDuration =
          values?.actualOut && values?.actualIn
            ? milli2DaysHrsMin(values.actualIn?.valueOf() - values.actualOut?.valueOf())
            : null;
        const airDuration =
          values?.actualOff && values?.actualOn
            ? milli2DaysHrsMin(values.actualOn?.valueOf() - values.actualOff?.valueOf())
            : null;
        const subtitle = blockDuration && airDuration ? `Block: ${blockDuration} / Air: ${airDuration}` : "";
        const updateDepartureGateCodes = (ident: string): void => {
          flightForm.setFieldValue("departureGate", "", false);
          flightForm.setFieldTouched("departureGate", false, false);
          const gates = airportOptions[ident]?.gates ?? [];
          const gateCodes = gates.map((gate) => {
            return { label: gate.code, value: gate.code };
          });
          setDepartureGateCodes(gateCodes);
        };

        const updateArrivalGateCodes = (ident: string): void => {
          flightForm.setFieldValue("arrivalGate", "", false);
          flightForm.setFieldTouched("arrivalGate", false, false);
          const gates = airportOptions[ident]?.gates ?? [];
          const gateCodes = gates.map((gate) => {
            return { label: gate.code, value: gate.code };
          });
          setArrivalGateCodes(gateCodes);
        };
        const suffixFieldHandlers = buildTransientTextFieldHandlers("flightNumberSuffix", flightForm);

        return (
          <Form style={{ width: "100%" }}>
            <FormikFormIsDirtyEffect updateParent={updateTabState} formDirtyState={dirty} />
            <CardContent id={`${mosaicId}-flight-editor`} className="form_card flight">
              <Grid container direction="column">
                <FormHeader title="Flight" subtitle={subtitle} />
                <DisplayLastModification
                  userId={values.modifiedBy ?? values.createdBy} // Use initial values here
                  time={values.modifiedAt ?? values.createdAt} // Use initial values here
                  released={values.release}
                  outsideModification={lastModifiedInfo}
                />
                <Grid container sx={FlightFormFieldsStyles(theme)} className={"form_body"}>
                  <Typography className="required_fields_label">required fields</Typography>

                  <Grid container direction="row" className="required_fields">
                    <Grid container wrap="nowrap">
                      <FormikTextField
                        label="Flight #"
                        name="flightNumber"
                        disabled={!hasPermissions(PolicyResource.Flight, "flightNumber")}
                      />
                      <FormikTextField
                        label="Flight # Suffix"
                        name="flightNumberSuffix"
                        onChangeDelay={0}
                        parse={(value: string) => {
                          if (!value) return "";
                          return value.slice(0, 1).toUpperCase();
                        }}
                        inputProps={{
                          maxLength: 1,
                          pattern: "[A-Za-z]",
                          onFocus: suffixFieldHandlers.onFocus,
                          onBlur: suffixFieldHandlers.onBlur,
                        }}
                        disabled={!hasPermissions(PolicyResource.Flight, "flightNumber")}
                      />
                      <FormikCheckboxField
                        name="isReleased"
                        label="Released"
                        disabled={!hasPermissions(PolicyResource.Flight, "isReleased")}
                      />
                    </Grid>
                    <FormikSelectField
                      className="flightTypegroup"
                      name="flightType"
                      label="Flight Type"
                      isMulti={false}
                      options={flightEventTypes}
                      getOptionLabel={(type: IFlightType) => `${type.title} - ${type.description}`}
                      getOptionValue={(flightType: IFlightType) => flightType._id}
                      placeholder="Select FlightType"
                      isSearchable={true}
                      disabled={!hasPermissions(PolicyResource.Flight, "flightType")}
                    />
                    <Grid container columnSpacing={0}>
                      <Grid item xs={7}>
                        <FormikSelectField
                          label="Origin"
                          name="origin"
                          className="airportSelection"
                          format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                          parse={(airport: IAirportOption) => airport?.ident}
                          onInputChange={(ident: string) => {
                            updateAirportOptions(ident, airportOptions, dispatch);
                            flightForm.setFieldValue("departureGate", "", false);
                            flightForm.setFieldTouched("departureGate", true);
                          }}
                          options={Object.values(airportOptions)}
                          getOptionLabel={(airport: IAirportOption) =>
                            `${airport.ident}${airport?.iata ? `/${airport.iata}` : ""} - ${airport.name}`
                          }
                          onChange={(ident: string) => {
                            updateDepartureGateCodes(ident);
                          }}
                          getOptionValue={(airport: IAirportOption) => airport.ident}
                          isSearchable={true}
                          menuPortalTarget={mosaicPane}
                          menuPosition={"fixed"}
                          openMenuOnFocus={false}
                          isClearable={false}
                          noOptionsMessage={() => "Search airport name or ICAO..."}
                          disabled={!hasPermissions(PolicyResource.Flight, "origin")}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FormikSelectField
                          label="Dep Gate"
                          name="departureGate"
                          className={"gate"}
                          isClearable
                          format={(gate) => {
                            if (!gate) {
                              if (fullFlight.origin === formikRef.current?.values.origin) {
                                return {
                                  label: fullFlight.departureGate,
                                  value: fullFlight.departureGate,
                                };
                              }
                            } else {
                              const code = departureGateCodes.find((g) => g.value === gate);
                              const newDepartureGateCode = {
                                label: gate,
                                value: gate,
                              };
                              return code ? code : newDepartureGateCode;
                            }
                          }}
                          parse={(gate: any) => gate?.value}
                          creatable={flightEditorSettings?.gateFreeFormTextInputEnabled ?? false}
                          onCreateOption={(inputVal: string) => {
                            if (flightEditorSettings.gateFreeFormTextInputEnabled === true && inputVal != "") {
                              const inUseAlready = departureGateCodes.find((g) => g.value === inputVal);

                              if (!inUseAlready) {
                                const newDepartureGateCode = {
                                  label: inputVal,
                                  value: inputVal,
                                };
                                const newDepartureGateCodes = [...departureGateCodes];
                                newDepartureGateCodes.push(newDepartureGateCode);
                                setDepartureGateCodes(newDepartureGateCodes);
                              }

                              flightForm.setFieldValue("departureGate", inputVal);
                              flightForm.setFieldTouched("departureGate", true);
                            }
                            return;
                          }}
                          isMulti={false}
                          options={departureGateCodes}
                          placeholder="Select Gate"
                          isSearchable={true}
                          disabled={!hasPermissions(PolicyResource.Flight, "departureGate")}
                        />
                      </Grid>
                    </Grid>
                    <Grid container columnSpacing={0}>
                      <Grid item xs={7}>
                        <FormikSelectField
                          label="Destination"
                          name="destination"
                          className="airportSelection"
                          format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                          parse={(airport: IAirportOption) => airport?.ident}
                          onInputChange={(ident: string) => {
                            updateAirportOptions(ident, airportOptions, dispatch);
                            flightForm.setFieldValue("arrivalGate", "", false);
                            flightForm.setFieldTouched("arrivalGate", true);
                          }}
                          onChange={(ident: string) => {
                            updateArrivalGateCodes(ident);
                          }}
                          options={Object.values(airportOptions)}
                          getOptionLabel={(airport: IAirportOption) =>
                            `${airport.ident}${airport?.iata ? `/${airport.iata}` : ""} - ${airport.name}`
                          }
                          getOptionValue={(airport: IAirportOption) => airport.ident}
                          isSearchable={true}
                          menuPortalTarget={mosaicPane}
                          menuPosition={"fixed"}
                          openMenuOnFocus={false}
                          isClearable={false}
                          disabled={!hasPermissions(PolicyResource.Flight, "destination")}
                        />{" "}
                      </Grid>
                      <Grid item xs={5}>
                        <FormikSelectField
                          label="Arr Gate"
                          name="arrivalGate"
                          className={"gate"}
                          isClearable
                          format={(gate) => {
                            if (!gate) {
                              if (fullFlight.destination === formikRef.current?.values.destination) {
                                return {
                                  label: fullFlight.arrivalGate,
                                  value: fullFlight.arrivalGate,
                                };
                              }
                            } else {
                              const code = arrivalGateCodes.find((g) => g.value === gate);
                              const newArrivalGateCode = {
                                label: gate,
                                value: gate,
                              };
                              return code ? code : newArrivalGateCode;
                            }
                          }}
                          parse={(gate: any) => gate?.value}
                          creatable={flightEditorSettings?.gateFreeFormTextInputEnabled ?? false}
                          onCreateOption={(inputVal: string) => {
                            if (flightEditorSettings.gateFreeFormTextInputEnabled === true && inputVal != "") {
                              const inUseAlready = arrivalGateCodes.find((g) => g.value === inputVal);

                              if (!inUseAlready) {
                                const newArrivalGateCode = {
                                  label: inputVal,
                                  value: inputVal,
                                };
                                const newArrivalGateCodes = [...departureGateCodes];
                                newArrivalGateCodes.push(newArrivalGateCode);
                                setArrivalGateCodes(newArrivalGateCodes);
                              }

                              flightForm.setFieldValue("arrivalGate", inputVal);
                              flightForm.setFieldTouched("arrivalGate", true);
                            }
                            return;
                          }}
                          isMulti={false}
                          options={arrivalGateCodes}
                          placeholder="Select Gate"
                          isSearchable={true}
                          disabled={!hasPermissions(PolicyResource.Flight, "arrivalGate")}
                        />
                      </Grid>
                    </Grid>

                    <CheckboxField
                      className="checkfield diverted"
                      name="isDiverted"
                      label="Diverted"
                      value={!!values?.divertedFrom}
                      onChange={() => handleSetDivertedDest(!values?.divertedFrom, flightForm)}
                      disabled={
                        !hasPermissions(PolicyResource.Flight, "divertedFrom") ||
                        !hasPermissions(PolicyResource.Flight, "diversionReason")
                      }
                    />

                    {!!values?.divertedFrom && (
                      <Grid container direction="column" style={{ width: 363 }}>
                        <FormikTextField label="Original Dest" name="divertedFrom" disabled={true} />
                        <FormikSelectField
                          label="Reason"
                          name="diversionReason"
                          options={diversionReasons}
                          getOptionLabel={(option: any) => option.title}
                          getOptionValue={(option: any) => option._id}
                          isSearchable={true}
                          menuPortalTarget={mosaicPane}
                          menuPosition={"fixed"}
                          openMenuOnFocus={false}
                          isClearable={false}
                        />
                        {/* Only show the diversionComment entry if the flight was not previously diverted */}
                        {!initialValues?.divertedFrom && (
                          <FormikTextField
                            className="field textfield multiline_long"
                            label="Comment"
                            name="diversionComment"
                            multiline={true}
                            minRows={3}
                            maxRows={5}
                          />
                        )}
                      </Grid>
                    )}

                    <Grid item>
                      {/* DISPLAY DATE/TIME LOCAL UTC SWITCH */}
                      <SwitchField
                        className={`time_mode_selector ${!displayAsUTC && "active"}`}
                        name={"editflight_time_mode_selector"}
                        value={displayAsUTC}
                        onChange={() => setDisplayAsUTC(!displayAsUTC)}
                        switchlabels={["UTC", "LOCAL"]}
                        switchtooltips={["Times as UTC", "Times as local"]}
                        label="Display Date/Times in"
                        allowNull={false}
                      />
                    </Grid>

                    <Grid container wrap="nowrap">
                      <FormikDateTimeField
                        className="field datetimefield blocktimes"
                        label="Sched Out"
                        name="scheduledOut"
                        parse={(value: number) => {
                          if (value) {
                            shiftOffOnTimes(value - values?.scheduledOut?.valueOf(), "scheduledOff", flightForm);
                          }
                          return value;
                        }}
                        showAirportTime={values?.origin}
                        localizeDisplay={!displayAsUTC}
                        hideLocalizeBtn={true}
                        isEpoch={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "scheduledOut")}
                      />
                      <TextField
                        className={"taxiTime no_arrows"}
                        label="Taxi Out"
                        name="taxiOut-scheduled"
                        value={
                          values?.scheduledOff && values?.scheduledOut
                            ? (values?.scheduledOff?.valueOf() - values?.scheduledOut?.valueOf()) / 60000
                            : undefined
                        }
                        onChange={(event) => {
                          const taxiMin: number = parseInt(event?.target?.value ?? "0");
                          computeFromTaxi(taxiMin, "scheduledOff", flightForm);
                          //if there is no departure message...
                          if (!values.receivedDepartureMessage) {
                            const newValue = values.actualOut?.valueOf() + taxiMin * minuteInMilliseconds;
                            flightForm.setFieldValue("actualOff", newValue);
                            flightForm.setFieldTouched("actualOff", true, true);
                          }
                        }}
                        inputProps={{ min: "0", max: "9999" }}
                        placeholder="min"
                        type="number"
                        meta={{
                          initialValue:
                            initialValues?.scheduledOff && initialValues?.scheduledOut
                              ? (initialValues?.scheduledOff?.valueOf() - initialValues?.scheduledOut?.valueOf()) /
                                60000
                              : undefined,
                          touched: initialValues?.scheduledOff !== values?.scheduledOff,
                        }}
                        disabled={!hasPermissions(PolicyResource.Flight, "scheduledOff")}
                      />
                    </Grid>

                    <Grid container wrap="nowrap">
                      <FormikDateTimeField
                        className="field datetimefield blocktimes"
                        label="Sched In"
                        name="scheduledIn"
                        parse={(value: number) => {
                          if (value) shiftOffOnTimes(value - values?.scheduledIn?.valueOf(), "scheduledOn", flightForm);
                          return value;
                        }}
                        showAirportTime={values?.destination}
                        localizeDisplay={!displayAsUTC}
                        hideLocalizeBtn={true}
                        isEpoch={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "scheduledIn")}
                      />
                      <TextField
                        className={"taxiTime no_arrows"}
                        label="Taxi In"
                        name="taxiIn-scheduled"
                        value={
                          values?.scheduledIn && values?.scheduledOn
                            ? (values?.scheduledIn?.valueOf() - values?.scheduledOn?.valueOf()) / 60000
                            : undefined
                        }
                        onChange={(event) => {
                          const taxiMin: number = parseInt(event?.target?.value ?? "0");
                          computeFromTaxi(taxiMin, "scheduledOn", flightForm);
                          //if you haven't recieved an arrival message yet
                          if (!values.receivedArrivalMessage) {
                            const newValue = values.actualIn?.valueOf() - taxiMin * minuteInMilliseconds;
                            flightForm.setFieldValue("actualOn", newValue);
                            flightForm.setFieldTouched("actualOn", true, true);
                          }
                        }}
                        inputProps={{ min: "0", max: "9999" }}
                        placeholder="min"
                        type="number"
                        meta={{
                          initialValue:
                            initialValues?.scheduledIn && initialValues?.scheduledOn
                              ? (initialValues?.scheduledIn?.valueOf() - initialValues?.scheduledOn?.valueOf()) / 60000
                              : undefined,
                          touched: initialValues?.scheduledOn !== values?.scheduledOn,
                        }}
                        disabled={!hasPermissions(PolicyResource.Flight, "scheduledOn")}
                      />
                    </Grid>

                    <Grid container wrap="nowrap">
                      <FormikDateTimeField
                        className="field datetimefield blocktimes"
                        label={values?.receivedDepartureMessage ? "Actual Out" : "Estimated Out"}
                        name="actualOut"
                        parse={(value: number) => {
                          if (value && !values?.receivedDepartureMessage) {
                            const newValue = value + (values.scheduledOff?.valueOf() - values.scheduledOut?.valueOf());
                            flightForm.setFieldValue("actualOff", newValue);
                            flightForm.setFieldTouched("actualOff", true, true);
                          }
                          return value;
                        }}
                        showAirportTime={values?.origin}
                        localizeDisplay={!displayAsUTC}
                        hideLocalizeBtn={true}
                        isEpoch={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "actualOut")}
                      />
                      {values?.receivedDepartureMessage && (
                        <TextField
                          className={"taxiTime no_arrows"}
                          label="Taxi Out"
                          name="taxiOut-actual"
                          value={
                            values?.actualOff && values?.actualOut
                              ? (values?.actualOff?.valueOf() - values?.actualOut?.valueOf()) / 60000
                              : undefined
                          }
                          inputProps={{ min: "0", max: "9999" }}
                          placeholder="min"
                          type="number"
                          meta={{
                            initialValue:
                              initialValues?.actualOff && initialValues?.actualOut
                                ? (initialValues?.actualOff?.valueOf() - initialValues?.actualOut?.valueOf()) / 60000
                                : undefined,
                            touched: initialValues?.actualOff !== values?.actualOff,
                          }}
                          disabled={true}
                        />
                      )}
                    </Grid>

                    <Grid container direction="column">
                      {values?.receivedDepartureMessage && (
                        <FormikDateTimeField
                          className="field datetimefield blocktimes"
                          label="Actual Off"
                          name="actualOff"
                          showAirportTime={values?.origin}
                          localizeDisplay={!displayAsUTC}
                          hideLocalizeBtn={true}
                          isEpoch={true}
                          disabled={!hasPermissions(PolicyResource.Flight, "actualOff")}
                        />
                      )}
                      <Grid item style={{ marginLeft: 88, width: 208 }}>
                        <FormikCheckboxField
                          name="receivedDepartureMessage"
                          label="Departure Message Received"
                          labelPlacement="end"
                          disabled={!hasPermissions(PolicyResource.Flight, "receivedDepartureMessage")}
                        />
                      </Grid>
                    </Grid>

                    {values?.receivedArrivalMessage && (
                      <FormikDateTimeField
                        className="field datetimefield blocktimes"
                        label="Actual On"
                        name="actualOn"
                        showAirportTime={values?.destination}
                        localizeDisplay={!displayAsUTC}
                        hideLocalizeBtn={true}
                        isEpoch={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "actualOn")}
                      />
                    )}
                    <Grid container wrap="nowrap">
                      <FormikDateTimeField
                        className="field datetimefield blocktimes"
                        label={values?.receivedArrivalMessage ? "Actual In" : "Estimated In"}
                        name="actualIn"
                        parse={(value: number) => {
                          if (value && !values?.receivedArrivalMessage) {
                            const newValue = value - (values.scheduledIn?.valueOf() - values.scheduledOn?.valueOf());
                            // Had to wrap a timeout here as the shift call was taking please directly after a formik onchange handler and validation wasn't properly getting called as a result.
                            setTimeout(() => {
                              flightForm.setFieldValue("actualOn", newValue);
                              flightForm.setFieldTouched("actualOn", true, true);
                            }, 0);
                          }
                          return value;
                        }}
                        showAirportTime={values?.destination}
                        localizeDisplay={!displayAsUTC}
                        hideLocalizeBtn={true}
                        isEpoch={true}
                        disabled={!hasPermissions(PolicyResource.Flight, "actualIn")}
                      />
                      {values?.receivedArrivalMessage && (
                        <TextField
                          className={"taxiTime no_arrows"}
                          label="Taxi In"
                          name="taxiIn-actual"
                          value={
                            values?.actualIn && values?.actualOn
                              ? (values?.actualIn?.valueOf() - values?.actualOn?.valueOf()) / 60000
                              : undefined
                          }
                          inputProps={{ min: "0", max: "9999" }}
                          placeholder="min"
                          type="number"
                          meta={{
                            initialValue:
                              initialValues?.actualIn && initialValues?.actualOn
                                ? (initialValues?.actualIn?.valueOf() - initialValues?.actualOn?.valueOf()) / 60000
                                : undefined,
                            touched: initialValues?.actualOn !== values?.actualOn,
                          }}
                          disabled={true}
                        />
                      )}
                    </Grid>
                    <Grid item style={{ marginLeft: 88, width: 208 }}>
                      <FormikCheckboxField
                        name="receivedArrivalMessage"
                        label="Arrival Message Received"
                        labelPlacement="end"
                        disabled={!hasPermissions(PolicyResource.Flight, "receivedArrivalMessage")}
                      />
                    </Grid>
                  </Grid>

                  <Grid container direction="row" className="optional_fields">
                    <FormikSelectField
                      name="aircraft"
                      label="Aircraft"
                      isMulti={false}
                      options={aircraftOptions}
                      onChange={(ac) => assignDefaultConfiguration(ac, flightForm)}
                      getOptionLabel={(aircraft: IFlightAircraft) =>
                        `${aircraft.registration} (${aircraft.aircraftType && aircraft.aircraftType.title})`
                      }
                      getOptionValue={(option: any) => option._id}
                      menuPortalTarget={mosaicPane}
                      menuPosition={"fixed"}
                      isSearchable={true}
                      isClearable={true}
                      disabled={!hasPermissions(PolicyResource.Flight, "aircraft")}
                    />
                    <FormikSelectField
                      name="aircraftConfiguration"
                      label="AC Config"
                      isMulti={false}
                      options={aircraftConfigOptions}
                      getOptionLabel={(option: any) => option.title}
                      getOptionValue={(option: any) => option._id}
                      menuPortalTarget={mosaicPane}
                      menuPosition={"fixed"}
                      isSearchable={true}
                      isClearable={true}
                      disabled={!hasPermissions(PolicyResource.Flight, "aircraftConfiguration")}
                    />
                    <FormikSelectField
                      name="customer"
                      label="Customer"
                      isMulti={false}
                      options={customers.sort((a, b) => {
                        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                      })}
                      getOptionLabel={(option: any) => option.name}
                      getOptionValue={(option: any) => option._id}
                      menuPortalTarget={mosaicPane}
                      menuPosition={"fixed"}
                      isSearchable={true}
                      isClearable={true}
                      disabled={!hasPermissions(PolicyResource.Flight, "customer")}
                    />
                    <FormikTextField
                      className="field textfield multiline_long"
                      label="Small Note"
                      name="note"
                      multiline={true}
                      minRows={3}
                      maxRows={5}
                      disabled={!hasPermissions(PolicyResource.Flight, "note")}
                    />
                    <FormikSelectField
                      name="flags"
                      label="Flags"
                      isMulti={true}
                      options={flightFlags.sort((a, b) => {
                        return a.description.toLowerCase().localeCompare(b.description.toLowerCase());
                      })}
                      parse={(option: any) => option ?? []}
                      getOptionLabel={(option: IFlightFlag) => {
                        return `${option.description} [ ${option.title} ]`;
                      }}
                      formatOptionLabel={(option: IFlightFlag) => {
                        return (
                          <Grid container alignItems="center" wrap="nowrap">
                            <Grid item className="flag_icon" style={{ width: "auto" }}>
                              {renderFlagIcon(option?.icon)}
                            </Grid>
                            <Grid
                              item
                              style={{ marginLeft: "15px", width: "auto" }}
                            >{`${option.description} [ ${option.title} ]`}</Grid>
                          </Grid>
                        );
                      }}
                      getOptionValue={(option: IFlightFlag) => option._id}
                      menuPlacement={"top"}
                      isSearchable={true}
                      isClearable={true}
                      disabled={!hasPermissions(PolicyResource.Flight, "flags")}
                    />
                  </Grid>

                  <Grid container direction="row" className="optional_fields"></Grid>

                  <Grid container className="form_footer">
                    <Button
                      className="btn reset"
                      type="reset"
                      variant="contained"
                      color="secondary"
                      disabled={(!dirty && isValid) || isSubmitting}
                      onClick={handleReset}
                    >
                      Reset
                    </Button>
                    <Button
                      className={"btn save"}
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || !isValid || !dirty}
                      type="submit"
                    >
                      {values?._id ? "Update" : "Create"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Form>
        );
      }}
    </Formik>
  );
};
